import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Select from '@lib/components/Select/Select';
import DatePicker from '@lib/components/DateTimePicker/DatePicker';
import { InputSizes } from '@lib/components/Input/enums';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import { transformDate } from '@lib/utils/dateTimeHelpers';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import useTrendNavigation from './useTrendNavigation';
import styles from './DocumentsViewingTrend.module.scss';

interface Props {
  /** [startDate, endDate] ISO strings from the parent analytics period filter. */
  period: string[];
  isDefaultPeriod: boolean;
  onClearPeriod: () => void;
  // Reports the first successful data load up to the page-level loader.
  onFirstLoaded?: () => void;
}

export default function DocumentsViewingTrend({
  period,
  isDefaultPeriod,
  onClearPeriod,
  onFirstLoaded,
}: Props) {
  const { t } = useTranslation();
  const {
    groupByOptions,
    selectedGroupByOption,
    onGroupByChange,
    pickerBucketStart,
    datePickerType,
    datePickerFormat,
    minBucketStart,
    maxBucketEnd,
    isYearLockedView,
    isDecrementDisabled,
    isIncrementDisabled,
    decrement,
    increment,
    onPickerChange,
    hasAnyData,
    isLoading,
    options,
    series,
    chartKey,
  } = useTrendNavigation({ period });

  // Fire `onFirstLoaded` once the trend query has settled.
  const firstLoadReportedRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !firstLoadReportedRef.current) {
      firstLoadReportedRef.current = true;
      onFirstLoaded?.();
    }
  }, [isLoading, onFirstLoaded]);

  const renderChart = () => {
    if (isLoading && !hasAnyData) {
      return (
        <div className={styles.graphRoot}>
          <CircleLoader />
        </div>
      );
    }
    if (!hasAnyData) {
      return (
        <div className={styles.graphRoot}>
          <EmptyState
            icon={IconName.SearchOff}
            title={
              isDefaultPeriod ? t('no-activity-yet') : t('no-results-found')
            }
            text={
              isDefaultPeriod
                ? t('no-activity-yet-text')
                : t('no-results-found-text')
            }
            actions={
              isDefaultPeriod
                ? undefined
                : [
                    <Button
                      key="clear"
                      buttonText={t('clear-filter')}
                      buttonType={ButtonTypes.primaryFilled}
                      onClick={onClearPeriod}
                    />,
                  ]
            }
          />
        </div>
      );
    }
    return (
      <CircleLoaderWrapper
        isLoading={isLoading}
        type={CircleLoaderWrapperType.blur}
      >
        <div className={styles.graphRoot}>
          <Chart
            key={chartKey}
            options={options}
            series={series}
            type="bar"
            width="100%"
            height="350px"
          />
        </div>
      </CircleLoaderWrapper>
    );
  };

  return (
    <>
      <PageHeader title={t('documents-viewing-trend-by-period')} />
      <PageBody>
        <WhiteBox verticalPaddings="small" horizontalPaddings="small">
          <div className={styles.head}>
            {!isYearLockedView && (
              <div className={styles.datePickerWrap}>
                <IconButton
                  icon="chevron_left"
                  rounded
                  type={IconButtonTypes.SecondaryFilled}
                  onClick={decrement}
                  disabled={isDecrementDisabled}
                />
                <div>
                  <DatePicker
                    name="trend-date-picker"
                    format={datePickerFormat}
                    onChange={onPickerChange}
                    pickerType={datePickerType}
                    size={InputSizes.medium}
                    value={transformDate(pickerBucketStart)}
                    minDate={minBucketStart}
                    maxDate={maxBucketEnd}
                    allowClear={false}
                  />
                </div>
                <IconButton
                  icon="chevron_right"
                  rounded
                  type={IconButtonTypes.SecondaryFilled}
                  onClick={increment}
                  disabled={isIncrementDisabled}
                />
              </div>
            )}
            <div>
              <Select
                name="trend-group-by"
                value={selectedGroupByOption}
                isSearchable={false}
                isClearable={false}
                size={InputSizes.medium}
                onChange={onGroupByChange}
                options={groupByOptions}
                menuPlacement="bottom"
                usePortal
              />
            </div>
          </div>
          {renderChart()}
        </WhiteBox>
      </PageBody>
    </>
  );
}
